// AbsencesView.jsx — View PURA da /absences (View/ViewModel à risca).
// Recebe { vm, actions } — sem fetch, sem cálculo de status, sem formatação de data.
// Contrato: absences.vm.js (@version 1). Estados cobertos: loading, error, empty,
// ready, ready+rows:[] com isFilteredEmpty, e singlePage (paginação some quando pageCount <= 1).
//
// Consistência com Serviços/Integrações/Casca: mesmo CARD_SX (raio, borda, sombra), mesma barra
// de filtros, mesmo StateBlock, mesmos chips de tint e mesmo par de IconButton por item.
// Neutros vêm de --bg-page/--divider/--text-* (papéis themeáveis) e NÃO da escala --slate-*,
// que é global fixa e clara — usá-la como cor de texto quebra temas de superfície escura.
//
// Formato: TABELA em lg+ e CARTÃO EMPILHADO abaixo disso. Os dois caminhos leem os mesmos campos.
// O corte é 'lg' (1200px), não 'md': as 8 colunas têm mínimo de 832px e o pane do Admin é o
// viewport menos o drawer (276px) e o padding (48px) — em 1100px de viewport sobram ~773px, e aí
// a tabela precisaria de scroll horizontal só para chegar nos botões de editar/excluir. Em lg o
// pane tem ~873px e a tabela cabe inteira. Abaixo disso o cartão empilhado é melhor mesmo.
import {
  Box, Typography, Button, Select, MenuItem, IconButton,
  CircularProgress, Tooltip, Divider,
} from '@mui/material';
import EventBusyRounded from '@mui/icons-material/EventBusyRounded';
import Add from '@mui/icons-material/Add';
import FilterAltOff from '@mui/icons-material/FilterAltOff';
import EditRounded from '@mui/icons-material/EditRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import SearchOffRounded from '@mui/icons-material/SearchOffRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import ReplayRounded from '@mui/icons-material/ReplayRounded';
import LightbulbOutlined from '@mui/icons-material/LightbulbOutlined';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import ArrowRightAltRounded from '@mui/icons-material/ArrowRightAltRounded';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import EventAvailableRounded from '@mui/icons-material/EventAvailableRounded';
import PlayCircleOutlineRounded from '@mui/icons-material/PlayCircleOutlineRounded';
import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';

// TÍTULO DE PÁGINA: <Typography variant="h1"> puro. Tamanho, peso, altura de linha e a redução
// no celular vivem no tema pinado (muiThemeConfig) — e a fonte do nicho entra aí, só aí.

const CARD_SX = {
  bgcolor: 'var(--surface)',
  border: '1px solid',
  borderColor: 'var(--divider)',
  borderRadius: { xs: 'var(--radius-card-mobile)', sm: 'var(--radius-card)' },
  boxShadow: 'var(--shadow-card)',
};

// Grid compartilhado entre cabeçalho e linhas (lg+). Datas em coluna fixa para alinhar os dígitos.
// Os mínimos somam 776px + 56px de gap = 832px, de propósito: o pane do Admin em 1280px de
// viewport tem ~956px (drawer 276 + padding), e em md (900px) ainda menos. Mínimos maiores fariam
// a coluna "Ações" exigir scroll horizontal — os botões de editar/excluir ficariam fora de alcance.
// Se algum dia entrar mais uma coluna, prefira cortar mais cedo para o cartão a crescer aqui.
const ROW_GRID = {
  display: 'grid',
  gridTemplateColumns: 'minmax(140px, 1.5fr) minmax(100px, 1fr) 92px 92px 76px minmax(80px, 1.2fr) 112px 84px',
  alignItems: 'center',
  columnGap: 'var(--space-1)',
};

const ACTION_BTN_SX = {
  width: 40, height: 40, color: 'var(--text-secondary)',
  '&:hover': { color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
};
const DELETE_BTN_SX = {
  width: 40, height: 40, color: 'var(--text-secondary)',
  '&:hover': { color: 'var(--color-error)', bgcolor: 'var(--tint-error-bg)' },
};

/* ── Chip de status ───────────────────────────────────────────────────── */
// tone vem do VM (info | success | neutral). O ícone acompanha o tom para o estado não ser
// comunicado SÓ por cor (a11y) — e o rótulo é sempre o statusLabel do VM.
const STATUS_TONE = {
  info: {
    bg: 'var(--tint-info-bg)', border: 'var(--tint-info-border)', fg: 'var(--tint-info-fg)',
    Icon: EventAvailableRounded,
  },
  success: {
    bg: 'var(--tint-success-bg)', border: 'var(--tint-success-border)', fg: 'var(--tint-success-fg)',
    Icon: PlayCircleOutlineRounded,
  },
  neutral: {
    bg: 'var(--bg-page)', border: 'var(--divider)', fg: 'var(--text-secondary)',
    Icon: CheckCircleOutlineRounded,
  },
};

/** Chip de estado — DOIS REGISTROS, um critério: o estado PEDE AÇÃO ou não.
 *   · error / warning  → pastilha preenchida (tint + fio + ícone): salta numa tabela de 40 linhas;
 *   · success / info / neutral → PONTO + rótulo, sem pastilha: a coluna fica calma e quem aparece
 *     é o dado. Numa lista longa, 40 pastilhas verdes competem com o conteúdo e escondem as duas
 *     vermelhas — que são as únicas que mudam o que o dono do negócio vai fazer agora.
 *  Estado nunca só por cor: o rótulo nomeia o estado nos dois registros (e a pastilha leva ícone).
 *  Vale igual nos dois modos: o ponto usa o -fg do tint, que é medido em claro e em escuro. */
const CHIP_FILLED = { error: true, warning: true };

function StatusChip({ tone, label }) {
  const t = STATUS_TONE[tone] || STATUS_TONE.neutral;
  const { Icon } = t;
  if (!CHIP_FILLED[tone]) return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, maxWidth: '100%', flexShrink: 0, minWidth: 0 }}>
      <Box aria-hidden="true" component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: t.fg, flexShrink: 0 }} />
      <Typography variant="caption" sx={{ color: tone === 'neutral' ? 'var(--text-secondary)' : 'var(--text-primary)', fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
    </Box>
  );
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.625,
        bgcolor: t.bg, border: '1px solid', borderColor: t.border,
        borderRadius: 999, px: 1.125, py: '3px', maxWidth: '100%',
      }}
    >
      <Icon sx={{ fontSize: 14, color: t.fg }} />
      <Typography variant="caption" sx={{ color: t.fg, fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
    </Box>
  );
}

/* ── Botões de ação de uma linha ──────────────────────────────────────── */
function RowActions({ row, a }) {
  const who = `${row.professionalName} — ${row.startLabel} a ${row.endLabel}`;
  return (
    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
      <Tooltip title="Editar">
        <IconButton aria-label={`Editar ausência de ${who}`} onClick={() => a.onEdit?.(row.id)} sx={ACTION_BTN_SX}>
          <EditRounded sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Excluir">
        <IconButton aria-label={`Excluir ausência de ${who}`} onClick={() => a.onDelete?.(row.id)} sx={DELETE_BTN_SX}>
          <DeleteOutlineRounded sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

/* ── Linha da tabela (md+) ────────────────────────────────────────────── */
function AbsenceRow({ row, labels, a }) {
  return (
    <Box
      component="li"
      sx={{
        ...ROW_GRID,
        minWidth: 832,
        px: 2, py: 1.25,
        borderTop: '1px solid', borderColor: 'var(--divider)',
        transition: 'background-color .15s',
        '&:hover': { bgcolor: 'var(--color-primary-soft)' },
      }}
    >
      <Box sx={{ minWidth: 0, pr: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>
          {row.professionalName}
        </Typography>
        {row.professionalSpecialty && (
          <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)' }}>
            {row.professionalSpecialty}
          </Typography>
        )}
      </Box>

      <Typography variant="body2" sx={{ color: 'var(--text-secondary)', overflowWrap: 'anywhere', pr: 1 }}>
        {row.placeName}
      </Typography>

      <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
        {row.startLabel}
      </Typography>
      <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
        {row.endLabel}
      </Typography>

      <Typography variant="body2" sx={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
        {row.durationLabel}
      </Typography>

      <Tooltip title={row.reason || ''} disableHoverListener={!row.reason}>
        <Typography
          variant="body2"
          sx={{
            color: row.reason ? 'var(--text-secondary)' : 'var(--text-secondary)',
            pr: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}
        >
          {row.reason || '—'}
        </Typography>
      </Tooltip>

      <Box sx={{ minWidth: 0 }}>
        <StatusChip tone={row.statusTone} label={row.statusLabel} />
      </Box>

      <RowActions row={row} a={a} />
    </Box>
  );
}

/* ── Cartão da ausência (xs/sm) ───────────────────────────────────────── */
function AbsenceCard({ row, labels, a }) {
  return (
    <Box
      component="li"
      sx={{
        ...CARD_SX,
        p: 2, display: 'flex', flexDirection: 'column', gap: 1.25,
        transition: 'box-shadow .15s, border-color .15s',
        '&:hover': { boxShadow: 'var(--shadow-hover)', borderColor: 'var(--color-primary-light)' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)', lineHeight: 1.3, overflowWrap: 'anywhere' }}>
            {row.professionalName}
          </Typography>
          {row.professionalSpecialty && (
            <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)' }}>
              {row.professionalSpecialty}
            </Typography>
          )}
        </Box>
        <StatusChip tone={row.statusTone} label={row.statusLabel} />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)', fontVariantNumeric: 'tabular-nums' }}>
          {row.startLabel}
        </Typography>
        <ArrowRightAltRounded sx={{ fontSize: 18, color: 'var(--text-secondary)' }} />
        <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)', fontVariantNumeric: 'tabular-nums' }}>
          {row.endLabel}
        </Typography>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}>
          <ScheduleRounded sx={{ fontSize: 15, color: 'var(--text-secondary)' }} />
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
            {row.durationLabel}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
        <PlaceOutlined sx={{ fontSize: 16, color: 'var(--text-secondary)', flexShrink: 0 }} />
        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>
          {row.placeName}
        </Typography>
      </Box>

      <Box>
        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)' }}>
          {labels.reason}
        </Typography>
        <Typography variant="body2" sx={{ color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>
          {row.reason || '—'}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: 'var(--divider)' }} />
      <RowActions row={row} a={a} />
    </Box>
  );
}

/* ── Barra de filtros ─────────────────────────────────────────────────── */
function FiltersBar({ filters, a }) {
  const f = filters || {};
  const selectSx = {
    flex: '1 1 240px', minWidth: 200, borderRadius: 'var(--radius-sm)',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--divider)' },
  };
  return (
    <Box
      sx={{
        ...CARD_SX,
        p: { xs: 1.5, sm: 1.75 }, mb: 'var(--space-2)',
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25,
      }}
    >
      <Select
        value={f.professionalId ?? ''}
        onChange={(e) => a.onFilterProfessional?.(e.target.value)}
        size="small"
        displayEmpty
        inputProps={{ 'aria-label': 'Filtrar por profissional' }}
        sx={selectSx}
      >
        <MenuItem value="">Todos os profissionais</MenuItem>
        {(f.professionals || []).map((o) => (
          <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>
        ))}
      </Select>

      <Select
        value={f.placeId ?? ''}
        onChange={(e) => a.onFilterPlace?.(e.target.value)}
        size="small"
        displayEmpty
        inputProps={{ 'aria-label': 'Filtrar por local' }}
        sx={selectSx}
      >
        <MenuItem value="">Todos os locais</MenuItem>
        {(f.places || []).map((o) => (
          <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>
        ))}
      </Select>

      <Button
        onClick={() => a.onClearFilters?.()}
        disabled={!f.canClear}
        startIcon={<FilterAltOff sx={{ fontSize: 18 }} />}
        sx={{
          minHeight: 40, px: 1.75, borderRadius: 'var(--radius-sm)',
          border: '1px solid', borderColor: 'var(--divider)',
          color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)', textTransform: 'none',
          '&:hover': { borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
        }}
      >
        Limpar
      </Button>
    </Box>
  );
}

/* ── Paginação ────────────────────────────────────────────────────────── */
function PaginationBar({ pagination, a }) {
  const p = pagination || {};
  const page = p.page || 1;
  const pageCount = p.pageCount || 1;
  return (
    <Box
      sx={{
        mt: 'var(--space-2)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 1.5, flexWrap: 'wrap',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
          Itens por página
        </Typography>
        <Select
          value={p.pageSize ?? ''}
          onChange={(e) => a.onChangePageSize?.(e.target.value)}
          size="small"
          inputProps={{ 'aria-label': 'Itens por página' }}
          sx={{
            minWidth: 76, borderRadius: 'var(--radius-sm)',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--divider)' },
          }}
        >
          {(p.pageSizeOptions || []).map((n) => (
            <MenuItem key={n} value={n}>{n}</MenuItem>
          ))}
        </Select>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
          {p.rangeLabel}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            aria-label="Página anterior"
            disabled={page <= 1}
            onClick={() => a.onChangePage?.(page - 1)}
            sx={{
              width: 40, height: 40, borderRadius: 'var(--radius-sm)',
              border: '1px solid', borderColor: 'var(--divider)', color: 'var(--text-secondary)',
              '&:hover': { color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
            }}
          >
            <ChevronLeftRounded sx={{ fontSize: 20 }} />
          </IconButton>
          <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)', px: 0.5, whiteSpace: 'nowrap' }}>
            {page} / {pageCount}
          </Typography>
          <IconButton
            aria-label="Próxima página"
            disabled={page >= pageCount}
            onClick={() => a.onChangePage?.(page + 1)}
            sx={{
              width: 40, height: 40, borderRadius: 'var(--radius-sm)',
              border: '1px solid', borderColor: 'var(--divider)', color: 'var(--text-secondary)',
              '&:hover': { color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
            }}
          >
            <ChevronRightRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}

/* ── Blocos de estado ─────────────────────────────────────────────────── */
function StateBlock({ icon, title, description, action }) {
  return (
    <Box sx={{ ...CARD_SX, px: 3, py: { xs: 5, sm: 7 }, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
      <Box
        sx={{
          width: 64, height: 64, borderRadius: '50%', mb: 2,
          bgcolor: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)', mb: 0.5 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', maxWidth: 420 }}>
          {description}
        </Typography>
      )}
      {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
    </Box>
  );
}

/* ── View ─────────────────────────────────────────────────────────────── */
export default function AbsencesView({ vm, actions }) {
  const a = actions || {};
  const rows = vm.rows || [];
  const labels = vm.columnLabels || {};
  const showFilters = vm.viewState === 'ready';
  const showPagination = vm.viewState === 'ready' && rows.length > 0 && (vm.pagination?.pageCount || 1) > 1;

  const newButton = (
    <Button
      onClick={() => a.onNew?.()}
      startIcon={<Add />}
      disableElevation
      sx={{
        bgcolor: 'var(--color-primary)', color: 'var(--on-primary)',
        borderRadius: 'var(--radius-sm)', px: 2.25, minHeight: 42,
        fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
        '&:hover': { bgcolor: 'var(--color-primary-dark)' },
      }}
    >
      {vm.actionLabel}
    </Button>
  );

  const clearButton = (
    <Button
      onClick={() => a.onClearFilters?.()}
      startIcon={<FilterAltOff sx={{ fontSize: 18 }} />}
      sx={{
        minHeight: 42, px: 2.25, borderRadius: 'var(--radius-sm)',
        border: '1px solid', borderColor: 'var(--divider)', color: 'var(--text-secondary)',
        fontWeight: 'var(--fw-medium)', textTransform: 'none',
        '&:hover': { borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
      }}
    >
      Limpar filtros
    </Button>
  );

  let body = null;

  if (vm.viewState === 'loading') {
    body = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress size={32} aria-label="Carregando ausências" sx={{ color: 'var(--color-primary)' }} />
      </Box>
    );
  } else if (vm.viewState === 'error') {
    body = (
      <StateBlock
        icon={<ErrorRounded sx={{ fontSize: 32, color: 'var(--color-error)' }} />}
        title="Não foi possível carregar as ausências"
        description={vm.errorMessage}
        action={(
          <Button
            onClick={() => a.onRetry?.()}
            startIcon={<ReplayRounded />}
            sx={{
              minHeight: 42, px: 2.25, borderRadius: 'var(--radius-sm)',
              border: '1px solid', borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)',
              fontWeight: 'var(--fw-bold)', textTransform: 'none',
              '&:hover': { bgcolor: 'var(--color-primary-soft)' },
            }}
          >
            Tentar novamente
          </Button>
        )}
      />
    );
  } else if (vm.viewState === 'empty') {
    body = (
      <StateBlock
        icon={<EventBusyRounded sx={{ fontSize: 30, color: 'var(--text-secondary)' }} />}
        title="Nenhuma ausência cadastrada"
        description="Cadastre férias, licenças e folgas para bloquear esses períodos na agenda e evitar agendamentos indevidos."
        action={newButton}
      />
    );
  } else if (rows.length === 0) {
    body = (
      <StateBlock
        icon={<SearchOffRounded sx={{ fontSize: 30, color: 'var(--text-secondary)' }} />}
        title={vm.emptyFilteredMessage}
        description={vm.isFilteredEmpty ? 'Revise os filtros para ver todas as ausências cadastradas.' : null}
        action={vm.filters?.canClear ? clearButton : null}
      />
    );
  } else {
    body = (
      <>
        {/* lg+ : tabela */}
        <Box sx={{ ...CARD_SX, display: { xs: 'none', lg: 'block' }, overflowX: 'auto', overflowY: 'hidden' }}>
          <Box sx={{ ...ROW_GRID, minWidth: 832, px: 2, py: 1.25, bgcolor: 'var(--bg-page)' }}>
            {[labels.professional, labels.place, labels.start, labels.end, labels.duration, labels.reason, labels.status].map((l) => (
              <Typography
                key={l}
                variant="caption"
                sx={{ color: 'var(--text-secondary)', fontWeight: 'var(--fw-bold)', letterSpacing: '.03em', textTransform: 'uppercase' }}
              >
                {l}
              </Typography>
            ))}
            <Typography
              variant="caption"
              sx={{
                color: 'var(--text-secondary)', fontWeight: 'var(--fw-bold)', letterSpacing: '.03em',
                textTransform: 'uppercase', textAlign: 'right',
              }}
            >
              {labels.actions}
            </Typography>
          </Box>
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {rows.map((row) => <AbsenceRow key={row.id} row={row} labels={labels} a={a} />)}
          </Box>
        </Box>

        {/* xs → md : cartões empilhados */}
        <Box
          component="ul"
          sx={{
            display: { xs: 'flex', lg: 'none' }, flexDirection: 'column', gap: 'var(--space-2)',
            listStyle: 'none', m: 0, p: 0,
          }}
        >
          {rows.map((row) => <AbsenceCard key={row.id} row={row} labels={labels} a={a} />)}
        </Box>

        {showPagination && <PaginationBar pagination={vm.pagination} a={a} />}
      </>
    );
  }

  return (
    <Box sx={{ color: 'var(--text-primary)' }}>
      <Box
        sx={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: 2, flexWrap: 'wrap', mb: 'var(--space-3)',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EventBusyRounded sx={{ fontSize: 30, color: 'var(--color-primary-dark)' }} />
            <Typography variant="h1" component="h1" sx={{ color: 'var(--text-primary)', textWrap: 'pretty' }}>
              {vm.title}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.5 }}>
            {vm.subtitle}
          </Typography>
        </Box>
        {vm.viewState !== 'empty' && newButton}
      </Box>

      {vm.hint !== '' && vm.hint && (
        <Box
          sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1.25,
            bgcolor: 'var(--tint-info-bg)', border: '1px solid', borderColor: 'var(--tint-info-border)',
            borderRadius: 'var(--radius-sm)', px: 2, py: 1.25, mb: 'var(--space-2)',
          }}
        >
          <LightbulbOutlined sx={{ fontSize: 20, color: 'var(--tint-info-fg)', flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: 'var(--tint-info-fg)' }}>
            {vm.hint}
          </Typography>
        </Box>
      )}

      {showFilters && <FiltersBar filters={vm.filters} a={a} />}
      {body}
    </Box>
  );
}
